import { Button, Callout, Modal, Switch, Text } from "@imagedesignai/neo";
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import type { SelfieView } from "./api-client.ts";
import {
  type CameraCanvas,
  type CameraMediaDevices,
  type CameraStream,
  type CameraVideoFrameSource,
  type CapturedCameraFrame,
  captureCameraFrame,
  createCameraSession,
  encodeCameraFrame,
  requestUserCamera,
} from "./camera-capture.ts";

type CameraStage = "requesting" | "live" | "captured" | "saving" | "error";

export interface CameraCaptureState {
  stage: CameraStage;
  frame: CapturedCameraFrame | null;
  previewUrl: string | null;
  mirrorSaved: boolean;
  error: string | null;
}

type CameraCaptureAction =
  | { type: "requesting" }
  | { type: "live" }
  | { type: "captured"; frame: CapturedCameraFrame; previewUrl: string }
  | { type: "mirror-changed"; mirrorSaved: boolean }
  | { type: "save-started" }
  | { type: "save-failed"; message: string }
  | { type: "failed"; message: string };

export function initialCameraCaptureState(): CameraCaptureState {
  return {
    stage: "requesting",
    frame: null,
    previewUrl: null,
    mirrorSaved: true,
    error: null,
  };
}

export function cameraCaptureReducer(
  state: CameraCaptureState,
  action: CameraCaptureAction,
): CameraCaptureState {
  switch (action.type) {
    case "requesting":
      return initialCameraCaptureState();
    case "live":
      return { ...state, stage: "live", error: null };
    case "captured":
      return {
        stage: "captured",
        frame: action.frame,
        previewUrl: action.previewUrl,
        mirrorSaved: true,
        error: null,
      };
    case "mirror-changed":
      return state.stage === "captured" ? { ...state, mirrorSaved: action.mirrorSaved } : state;
    case "save-started":
      return state.stage === "captured" ? { ...state, stage: "saving", error: null } : state;
    case "save-failed":
      return state.stage === "saving"
        ? { ...state, stage: "captured", error: action.message }
        : state;
    case "failed":
      return { ...initialCameraCaptureState(), stage: "error", error: action.message };
  }
}

interface CameraSelfieSaveDependencies {
  encodeFrame(frame: CapturedCameraFrame, mirror: boolean): Promise<Blob>;
  saveFile(file: File): Promise<SelfieView>;
  now(): Date;
}

function cameraFilename(now: Date): string {
  return `facey-${now.toISOString().replace(/[:.]/g, "-")}.jpg`;
}

export async function saveCameraSelfie(
  frame: CapturedCameraFrame,
  mirrorSaved: boolean,
  deps: CameraSelfieSaveDependencies,
): Promise<SelfieView> {
  const blob = await deps.encodeFrame(frame, mirrorSaved);
  const file = new File([blob], cameraFilename(deps.now()), { type: "image/jpeg" });
  return await deps.saveFile(file);
}

export function CameraSelfieReview({
  previewUrl,
  mirrorSaved,
  disabled,
  error,
  onMirrorChange,
  onRetake,
  onSave,
}: {
  previewUrl: string;
  mirrorSaved: boolean;
  disabled: boolean;
  error?: string | null;
  onMirrorChange(mirror: boolean): void;
  onRetake(): void;
  onSave(): void;
}) {
  return (
    <div className="camera-review">
      <div className="camera-stage">
        <img
          className={`camera-review-image${mirrorSaved ? " camera-mirrored" : ""}`}
          src={previewUrl}
          alt="Captured selfie preview"
        />
      </div>
      <Switch
        label="Mirror saved photo"
        description="Turn this off to save the camera frame in its original orientation."
        checked={mirrorSaved}
        disabled={disabled}
        onChange={(event) => onMirrorChange(event.currentTarget.checked)}
      />
      {error ? (
        <Callout intent="danger" label="Could not save selfie">
          {error}
        </Callout>
      ) : null}
      <div className="camera-actions">
        <Button type="button" intent="secondary" size="sm" disabled={disabled} onClick={onRetake}>
          Retake
        </Button>
        <Button
          type="button"
          intent="positive"
          size="sm"
          icon="check"
          loading={disabled}
          onClick={onSave}
        >
          Use photo
        </Button>
      </div>
    </div>
  );
}

interface CameraPorts {
  requestStream(): Promise<CameraStream>;
  captureFrame(video: CameraVideoFrameSource): CapturedCameraFrame;
  encodeFrame(frame: CapturedCameraFrame, mirror: boolean): Promise<Blob>;
  createPreviewUrl(blob: Blob): string;
  revokePreviewUrl(url: string): void;
  now(): Date;
}

const browserCameraPorts: CameraPorts = {
  requestStream: async () =>
    await requestUserCamera(globalThis.navigator?.mediaDevices as CameraMediaDevices | undefined),
  captureFrame: (video) =>
    captureCameraFrame(video, () => document.createElement("canvas") as CameraCanvas),
  encodeFrame: async (frame, mirror) =>
    await encodeCameraFrame(frame, mirror, () => document.createElement("canvas") as CameraCanvas),
  createPreviewUrl: (blob) => URL.createObjectURL(blob),
  revokePreviewUrl: (url) => URL.revokeObjectURL(url),
  now: () => new Date(),
};

export function CameraSelfieModal({
  onClose,
  onSaved,
  saveFile,
  ports = browserCameraPorts,
}: {
  onClose(): void;
  onSaved(selfie: SelfieView): void;
  saveFile(file: File): Promise<SelfieView>;
  ports?: CameraPorts;
}) {
  const [state, dispatch] = useReducer(cameraCaptureReducer, undefined, initialCameraCaptureState);
  const [isCapturing, setIsCapturing] = useState(false);
  const [stream, setStream] = useState<CameraStream | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const previewUrlRef = useRef<string | null>(null);
  const isMounted = useRef(false);
  const session = useMemo(
    () =>
      createCameraSession({
        requestStream: ports.requestStream,
        captureFrame: ports.captureFrame,
      }),
    [ports],
  );

  const releasePreview = useCallback(() => {
    if (!previewUrlRef.current) return;
    ports.revokePreviewUrl(previewUrlRef.current);
    previewUrlRef.current = null;
  }, [ports]);

  const beginCamera = useCallback(async () => {
    releasePreview();
    setStream(null);
    dispatch({ type: "requesting" });
    try {
      const nextStream = await session.open();
      if (!isMounted.current || !nextStream) return;
      setStream(nextStream);
    } catch (reason) {
      if (!isMounted.current) return;
      const message = reason instanceof Error ? reason.message : String(reason);
      dispatch({ type: "failed", message });
    }
  }, [releasePreview, session]);

  useEffect(() => {
    if (!stream) return;
    const video = videoRef.current;
    if (!video) {
      session.close();
      setStream(null);
      dispatch({ type: "failed", message: "Camera preview is unavailable." });
      return;
    }

    let isCurrent = true;
    video.srcObject = stream as MediaStream;
    void video.play().then(
      () => {
        if (isCurrent && isMounted.current) dispatch({ type: "live" });
      },
      (reason) => {
        if (!isCurrent || !isMounted.current) return;
        session.close();
        setStream(null);
        const message = reason instanceof Error ? reason.message : String(reason);
        dispatch({ type: "failed", message });
      },
    );

    return () => {
      isCurrent = false;
      if (video.srcObject === (stream as MediaStream)) video.srcObject = null;
    };
  }, [session, stream]);

  useEffect(() => {
    isMounted.current = true;
    void beginCamera();
    return () => {
      isMounted.current = false;
      session.close();
      releasePreview();
    };
  }, [beginCamera, releasePreview, session]);

  const close = () => {
    session.close();
    setStream(null);
    releasePreview();
    onClose();
  };

  const capture = async () => {
    const video = videoRef.current;
    if (!video || state.stage !== "live" || isCapturing) return;
    setIsCapturing(true);
    try {
      const frame = session.capture(video);
      setStream(null);
      const preview = await ports.encodeFrame(frame, false);
      if (!isMounted.current) return;
      const previewUrl = ports.createPreviewUrl(preview);
      previewUrlRef.current = previewUrl;
      dispatch({ type: "captured", frame, previewUrl });
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : String(reason);
      if (isMounted.current) dispatch({ type: "failed", message });
    } finally {
      if (isMounted.current) setIsCapturing(false);
    }
  };

  const save = async () => {
    if (state.stage !== "captured" || !state.frame) return;
    dispatch({ type: "save-started" });
    try {
      const selfie = await saveCameraSelfie(state.frame, state.mirrorSaved, {
        encodeFrame: ports.encodeFrame,
        saveFile,
        now: ports.now,
      });
      if (!isMounted.current) return;
      session.close();
      setStream(null);
      releasePreview();
      onSaved(selfie);
    } catch (reason) {
      const message = reason instanceof Error ? reason.message : String(reason);
      if (isMounted.current) dispatch({ type: "save-failed", message });
    }
  };

  return (
    <Modal title="Take a selfie" onClose={close} loading={state.stage === "requesting"}>
      <div className="camera-capture">
        {state.stage === "captured" || state.stage === "saving" ? (
          state.previewUrl ? (
            <CameraSelfieReview
              previewUrl={state.previewUrl}
              mirrorSaved={state.mirrorSaved}
              disabled={state.stage === "saving"}
              error={state.error}
              onMirrorChange={(mirrorSaved) => dispatch({ type: "mirror-changed", mirrorSaved })}
              onRetake={() => void beginCamera()}
              onSave={() => void save()}
            />
          ) : null
        ) : (
          <>
            <div className="camera-stage">
              {state.stage !== "error" ? (
                <video
                  ref={videoRef}
                  className="camera-video camera-mirrored"
                  autoPlay
                  muted
                  playsInline
                />
              ) : null}
              <svg className="camera-guide" viewBox="0 0 300 400" aria-hidden="true">
                <ellipse cx="150" cy="116" rx="52" ry="64" />
                <path d="M48 354c8-92 47-142 102-142s94 50 102 142" />
                <path d="M96 238c-28 10-48 30-58 64M204 238c28 10 48 30 58 64" />
              </svg>
            </div>
            <Text kind="body" tone="secondary">
              Leave some space above your head, keep both shoulders visible, and include about half
              your torso inside the guide.
            </Text>
            {state.stage === "error" && state.error ? (
              <Callout intent="danger" label="Camera unavailable">
                {state.error} Close this dialog to use the upload button instead.
              </Callout>
            ) : null}
            <div className="camera-actions">
              {state.stage === "error" ? (
                <Button
                  type="button"
                  intent="secondary"
                  size="sm"
                  onClick={() => void beginCamera()}
                >
                  Try camera again
                </Button>
              ) : (
                <Button
                  type="button"
                  intent="primary"
                  size="md"
                  icon="camera"
                  disabled={state.stage !== "live" || isCapturing}
                  loading={isCapturing}
                  onClick={() => void capture()}
                >
                  Capture photo
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
